// packages/console/src/components/panels/LivePresetPicker.tsx
//
// Live-preset picker for the LIVE console. It lists the workspace's live presets
// (mode==="live") and selects the one the Rendition panel below edits. It is the
// live-mode analogue of the file console's ProfileMenu, trimmed to a selection
// list: the live ladder is defined by the picked preset.
//
// Honest states (R10): loading shows a loading line, a load error shows the
// message, and an empty workspace (no live presets yet) says so rather than
// fabricating a default.

import { Panel } from "../Parts";
import { Icon } from "../Icons";
import type { Preset } from "../../api/jobs";

// CODEC_RANK orders the picker by codec family: AV1, then HEVC, then AVC (h264),
// so the list reads newest-codec-first and the default selection lands on AV1.
const CODEC_RANK: Record<string, number> = { av1: 0, hevc: 1, h264: 2 };

export function LivePresetPicker({
  presets,
  activeId,
  runningId,
  boundId,
  onApply,
  onApplyRestart,
  onAir,
  applying,
  onChange,
  loading,
  errorMessage,
}: {
  presets: Preset[];
  activeId: string | null;
  // runningId is the preset the LIVE stream is actually encoding with (null when
  // nothing is on air). It gets a LIVE badge so the running profile is never
  // confused with the one merely selected for editing (activeId).
  runningId?: string | null;
  // boundId is the preset the current stream is bound to (what the next Go-live
  // will encode with), even when stopped. When it differs from activeId, the
  // Apply control rebinds the stream to the selected profile. undefined when no
  // stream is selected (Apply is then hidden).
  boundId?: string | null;
  // onApply persists the selected preset as the stream's profile (stopped case:
  // takes effect on the next Go-live). onApplyRestart persists it AND re-arms the
  // stream (on-air case: switches profile now, briefly interrupting the broadcast).
  onApply?: (presetId: string) => void;
  onApplyRestart?: (presetId: string) => void;
  // onAir selects which action the Apply button performs: while on air a profile
  // change needs a re-arm, so it applies-and-restarts; otherwise it just persists.
  onAir?: boolean;
  applying?: boolean;
  onChange: (id: string) => void;
  loading: boolean;
  errorMessage: string | null;
}) {
  // Apply is offered when a stream is selected and the picked preset is not the
  // one it is already bound to. A rebind takes effect on the next Go-live (stopped)
  // or immediately via a re-arm (on air).
  const canOfferApply = !!onApply && boundId !== undefined && !!activeId && activeId !== boundId;
  const ordered = [...presets].sort((a, b) => {
    const ra = CODEC_RANK[a.videoCodec] ?? 9;
    const rb = CODEC_RANK[b.videoCodec] ?? 9;
    return ra - rb || a.name.localeCompare(b.name);
  });
  return (
    <Panel title="Live presets" style={{ flex: "none" }}>
      {loading ? (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>Loading live presets...</div>
      ) : errorMessage ? (
        <div style={{ font: "var(--t-micro)", color: "var(--err)" }}>{errorMessage}</div>
      ) : presets.length === 0 ? (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>No live presets yet for this workspace.</div>
      ) : (
        <div
          role="listbox"
          aria-label="Live preset"
          style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 176, overflowY: "auto" }}
        >
          {ordered.map((p) => {
            const active = p.id === activeId;
            const live = !!runningId && p.id === runningId;
            // The stream's bound profile gets a CURRENT tag when it is not the
            // live one, so the operator can see which preset a restart will use.
            const bound = !live && boundId !== undefined && p.id === boundId;
            return (
              <button
                key={p.id}
                className="ae-b"
                role="option"
                aria-selected={active}
                onClick={() => onChange(p.id)}
                title={p.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  minHeight: 34,
                  padding: "0 10px",
                  borderRadius: "var(--r-sm)",
                  cursor: "pointer",
                  textAlign: "left",
                  font: "400 12px var(--font-mono)",
                  color: active ? "#fff" : "var(--fg2)",
                  background: active ? "var(--blue-tint)" : "transparent",
                  border: `1px solid ${active ? "var(--blue-500)" : "var(--line-strong)"}`,
                }}
              >
                {active && <Icon name="check" size={13} color="var(--blue-300)" />}
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.name}
                </span>
                {live && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      flex: "none",
                      font: "700 8px var(--font-sans)",
                      letterSpacing: ".12em",
                      color: "#fff",
                      background: "rgba(244,63,94,.9)",
                      padding: "2px 5px",
                      borderRadius: 3,
                    }}
                  >
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />
                    LIVE
                  </span>
                )}
                {bound && (
                  <span
                    style={{
                      flex: "none",
                      font: "700 8px var(--font-sans)",
                      letterSpacing: ".12em",
                      color: "var(--fg3)",
                      background: "var(--bg-input)",
                      border: "1px solid var(--line-strong)",
                      padding: "2px 5px",
                      borderRadius: 3,
                    }}
                  >
                    CURRENT
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {canOfferApply && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 5 }}>
          <button
            onClick={() => {
              if (applying || !activeId) return;
              if (onAir) onApplyRestart?.(activeId);
              else onApply?.(activeId);
            }}
            disabled={applying}
            title={
              onAir
                ? "Switch this stream to the selected profile now (restarts the broadcast)"
                : "Bind this stream to the selected profile (takes effect on next Go Live)"
            }
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "var(--r-sm)",
              border: "1px solid var(--blue-500)",
              background: "var(--blue-tint)",
              color: "var(--blue-300)",
              font: "var(--t-btn)",
              letterSpacing: "var(--ls-btn)",
              textTransform: "uppercase",
              cursor: applying ? "not-allowed" : "pointer",
            }}
          >
            {applying ? (onAir ? "Restarting..." : "Applying...") : onAir ? "Apply & restart" : "Apply profile to stream"}
          </button>
          <span style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>
            {onAir ? "Restarts the broadcast to switch profile." : "Takes effect on the next Go Live."}
          </span>
        </div>
      )}
    </Panel>
  );
}
